import { UseFormReturnType } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import Braintree from "braintree-web";
import React from "react";
import { BraintreeFields } from "../../../../hooks/useBraintree";
import { RpiNotification, useRpiNotifications } from "../../../../hooks/useRpiNotifications";
import { isString } from "../../../../utils/type.util";

export type PaymentFormValues = Omit<
  GraphQL.BraintreePaymentMethodInput,
  "paymentMethodNonce" | "deviceDataFromClient"
> & {
  agreeToTerms: boolean;
};

export const requiredField = (errorMessage: string) => (value: string | undefined) =>
  (value?.length ?? 0) > 0 ? null : errorMessage;

interface UsePaymentFormSubmissionOptions {
  form: UseFormReturnType<PaymentFormValues>;
  hostedFields?: Braintree.HostedFields;
  dataCollector?: Braintree.DataCollector;
  getPayload: () => Promise<{
    payload: Braintree.HostedFieldsTokenizePayload;
    deviceData: string | object;
  }>;
  onComplete: () => void;
}

interface UsePaymentFormSubmissionReturn {
  isLoading: boolean;
  handleSubmit: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  cardError: string | undefined;
  braintreeErrors: Partial<Record<BraintreeFields, string>>;
  createPaymentMethod: ReturnType<typeof GraphQL.useCreatePaymentMethodMutation>;
  onSubmit: (values: PaymentFormValues) => Promise<void>;
  formRef: React.MutableRefObject<HTMLFormElement | null>;
}

export const usePaymentFormSubmission = ({
  form,
  hostedFields,
  getPayload,
  onComplete,
}: UsePaymentFormSubmissionOptions): UsePaymentFormSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [braintreeErrors, setBraintreeErrors] = React.useState<Partial<Record<BraintreeFields, string>>>({});
  const [cardError, setCardError] = React.useState<string>();
  const { refetch: refetchBulletins } = useRpiNotifications();

  const formRef = React.useRef<HTMLFormElement | null>(null);
  const braintreeDataRef = React.useRef<{
    nonce: string;
    deviceData: string;
  } | null>(null);

  // Clear Braintree errors when user starts typing in a field
  React.useEffect(() => {
    if (!hostedFields) return;

    const clearFieldError = (event: Braintree.HostedFieldsEvent) => {
      const fieldName = event.emittedBy as BraintreeFields;
      setBraintreeErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    };

    hostedFields.on("notEmpty", clearFieldError);

    return () => {
      hostedFields.off("notEmpty", clearFieldError);
    };
  }, [hostedFields]);

  const createPaymentMethod = GraphQL.useCreatePaymentMethodMutation<{
    message: string;
  }>({
    onSuccess: () => {
      refetchBulletins(RpiNotification.PAYMENT_METHOD);
      onComplete();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  // Unified loading state: tokenization and mutation
  const isLoading = isSubmitting || createPaymentMethod.isLoading;

  const handleSubmit = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      braintreeDataRef.current = null;
      createPaymentMethod.reset();
      let hasBraintreeErrors = false;
      setIsSubmitting(true);

      try {
        const { payload, deviceData } = await getPayload();
        braintreeDataRef.current = {
          nonce: payload.nonce,
          deviceData: String(deviceData),
        };
        setBraintreeErrors({});
        setCardError(undefined);
      } catch (error) {
        console.log("braintreeErrors", error);
        hasBraintreeErrors = true;

        if (isString(error)) {
          setCardError(error);
        } else {
          setBraintreeErrors(error as Partial<Record<BraintreeFields, string>>);
        }
      }

      const validationResult = form.validate();

      if (hasBraintreeErrors || validationResult.hasErrors) {
        setIsSubmitting(false);
        return;
      }

      formRef.current?.requestSubmit();
    },
    [createPaymentMethod, getPayload, form]
  );

  const onSubmit = React.useCallback(
    async (values: PaymentFormValues) => {
      // Strip out frontend-only field (agreeToTerms) before sending to API
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { agreeToTerms, ...input } = values;

      const inputWithBraintreeData: GraphQL.BraintreePaymentMethodInput = {
        ...input,
        paymentMethodNonce: braintreeDataRef.current?.nonce ?? "",
        deviceDataFromClient: braintreeDataRef.current?.deviceData,
      };

      return createPaymentMethod.mutate({ input: inputWithBraintreeData });
    },
    [createPaymentMethod]
  );

  return {
    isLoading,
    handleSubmit,
    cardError,
    braintreeErrors,
    createPaymentMethod,
    onSubmit,
    formRef,
  };
};
